import { Group, Paper, ScrollArea, Stack, Tabs } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { useMyForm } from '../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../react-commons/query/QueryBaseComponent';
import {
    defaultProjectResource,
    ProjectMutationResourceSchema,
    useProjectActions,
} from '../../../resource/resources/artifacts/project/projectResource';
import { useProjectVersionPublishmentActions } from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import { useStandardActions } from '../../../resource/resources/artifacts/standard/standardResource';
import { useUseCaseActions } from '../../../resource/resources/artifacts/use_case/useCaseResource';
import { useUserActions } from '../../../resource/resources/persistables/user/userResource';
import { useUserId } from '../../authentication/useUserId';
import { ProjectContent } from '../../page-components/project/ProjectContent';
import { ProjectReusableElementsComponent } from '../../page-components/project/ProjectReusableElementsComponent';
import { ProjectStructureComponent } from '../../page-components/project/ProjectStructureComponent';
import { ConditionalComponent } from '../../reusable-components/ConditionalComponent';
import { Form_EditableDisplayBar } from '../../reusable-components/form-components/Form_EdiableDisplayBar';
import { useMyNavigate } from '../../router/createdRouter';

export const ProjectComponent = () => {
    const userId = useUserId('');
    const isAuthenticated = Boolean(userId);
    const { id: projectId, versionId } = useParams<{ id: string; versionId: string | undefined }>();
    const [activeTab, setActiveTab] = useState<string | null>('structure');

    const navigate = useMyNavigate();

    const projectActions = useProjectActions();

    const projectVersionActions = useProjectVersionActions();
    const userActions = useUserActions();
    const standardActions = useStandardActions();
    const useCaseActions = useUseCaseActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();

    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const projectQuery = projectActions.useGetResource(projectId);
    const projectsQuery = projectActions.useGetAllResources();
    const projectVersionPublishment = projectVersionPublishmentActions.useGetSingleResource(versionId);
    const projectVersionQuery = projectVersionActions.useGetResource(versionId);
    const projectVersionsQuery = projectVersionActions.useGetAllResources(projectId);
    const getProjectVersion = () => {
        if (projectVersionQuery.data) {
            return projectVersionQuery.data;
        }

        const versionData = projectVersionsQuery.data;
        return versionData?.length ? versionData.at(-1) : undefined;
    };
    const updateProject = projectActions.useUpdateResource();

    const standardsQuery = standardActions.useGetAllResources(getProjectVersion()?.id);
    const useCasesQuery = useCaseActions.useGetAllUseCasesByStandardAssociationAndProjectVersion(
        getProjectVersion()?.id,
        standardsQuery.data?.length === 1 ? standardsQuery.data[0].objectId : undefined
    );

    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;

    const isManager = userSystemRights.data?.isManager;

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProjectMutationResourceSchema,
        defaultValues: { ...defaultProjectResource },
        submitHandler: async (data) => {
            await updateProject.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: projectQuery.data,
    });

    const getContentHeight = () =>
        isProcessAdmin ?
            projectVersionPublishment.data ?
                250
            :   200
        : projectVersionPublishment.data ? 200
        : 150;

    useEffect(() => {
        if (!isProcessAdmin && !isManager) {
            if (standardsQuery.data?.length === 1) {
                const standard = standardsQuery.data[0];
                if (useCasesQuery.data?.length === 1) {
                    const useCase = useCasesQuery.data[0];
                    const versionId = getProjectVersion()?.id;
                    history.replaceState('content', 'title', `/projects`);
                    navigate({
                        route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations',
                        routeParams: {
                            id: projectId ?? '',
                            versionId: versionId ?? '',
                            standardId: standard.objectId,
                            useCaseId: useCase.id,
                        },
                        searchParams: { redirected: 'true' },
                    });
                }
            }
        }
    }, [getProjectVersion, isManager, isProcessAdmin, projectId, standardsQuery.data, navigate, useCasesQuery.data]);

    return (
        <div className={'main-window-content'}>
            <QueryBaseComponent name={'Project'} query={userSystemRights}>
                <ConditionalComponent label={'Please login to see Project'} condition={isAuthenticated}>
                    <Paper p={'xs'}>
                        <Stack>
                            <div style={{ maxHeight: getContentHeight(), minHeight: getContentHeight() }}>
                                <form style={{ height: getContentHeight() }} onSubmit={onSubmit}>
                                    <Stack h={getContentHeight()} gap={'xs'}>
                                        <ScrollArea
                                            mih={projectVersionPublishment.data ? 200 : 150}
                                            mah={projectVersionPublishment.data ? 200 : 150}
                                            scrollbars={'y'}
                                            offsetScrollbars={'y'}
                                            type="auto"
                                        >
                                            <ProjectContent
                                                id={projectId}
                                                versionId={getProjectVersion()?.id}
                                                versionInfo={getProjectVersion()?.versionInfo}
                                                form={form}
                                                formNames={formNames}
                                                readOnly={!isProcessAdmin}
                                                hideName={projectsQuery.data?.length === 1 && !isProcessAdmin}
                                            />
                                        </ScrollArea>
                                        <Show when={isProcessAdmin}>
                                            <Group justify="flex-end">
                                                <Form_EditableDisplayBar form={form} canSubmitDefaultValue={false} />
                                            </Group>
                                        </Show>
                                    </Stack>
                                </form>
                            </div>
                            <Show when={!isProcessAdmin}>
                                <ProjectStructureComponent
                                    projectId={projectId}
                                    projectVersionId={getProjectVersion()?.id}
                                />
                            </Show>
                            <Show when={isProcessAdmin}>
                                <Tabs value={activeTab} onChange={setActiveTab} style={{ flexGrow: 1 }}>
                                    <Tabs.List>
                                        <Tabs.Tab value={'structure'}>Structure</Tabs.Tab>
                                        <Tabs.Tab value={'elements'}>Reusable Elements</Tabs.Tab>
                                    </Tabs.List>
                                    <Tabs.Panel value={'structure'}>
                                        <ProjectStructureComponent
                                            projectId={projectId}
                                            projectVersionId={getProjectVersion()?.id}
                                        />
                                    </Tabs.Panel>
                                    <Tabs.Panel value={'elements'}>
                                        <ProjectReusableElementsComponent
                                            projectId={projectId}
                                            projectVersionId={getProjectVersion()?.id}
                                            showing={activeTab}
                                        />
                                    </Tabs.Panel>
                                </Tabs>
                            </Show>
                        </Stack>
                    </Paper>
                </ConditionalComponent>
            </QueryBaseComponent>
        </div>
    );
};
